import { DATA_KEYS, readJSON, writeJSON } from "@/storage/local";
import { getExerciseOptions } from "@/data/defaultRoutine";
import { groupsOf } from "@/storage/weeklyGoal";

// Historial de ciclos de entrenamiento.
//
// Una "semana" aquí no es la del calendario: es una vuelta completa a la
// rutina. Cuando se han marcado todas las series de todos los días, ese ciclo
// se archiva y empieza el siguiente. Por eso puede haber semanas de tres días
// y semanas de diez.

const MAX_WEEKS = 104; // dos años de ciclos

export function loadWeeks() {
  const weeks = readJSON(DATA_KEYS.WEEKS, []);
  return Array.isArray(weeks) ? weeks : [];
}

// Congela lo hecho en el ciclo: qué ejercicio, con qué peso y cuántas reps.
// Se guarda el nombre resuelto de la variante elegida, no el del ejercicio
// base, porque es lo que de verdad se entrenó ese día.
//
// La exportación lo reutiliza para retratar el ciclo en curso, que todavía no
// está archivado, con la misma forma que los ya cerrados.
export function snapshotCycle(routine, progress, alternatives) {
  const days = [];
  const volumeByGroup = {};

  routine.forEach((day, dIdx) => {
    const exercises = [];

    day.exercises.forEach((ex) => {
      const options = getExerciseOptions(ex);
      const altIdx = alternatives[`${dIdx}-${ex.id}`] || 0;
      const active = options ? options[altIdx] || options[0] : null;

      const sets = [];
      for (let s = 0; s < ex.sets; s++) {
        const p = progress[`${dIdx}-${ex.id}-${s}`];
        if (!p?.done) continue;
        sets.push({ weight: p.weight || 0, reps: p.reps || 0 });
        groupsOf(ex.muscleGroup).forEach((g) => {
          volumeByGroup[g] = (volumeByGroup[g] || 0) + 1;
        });
      }

      if (sets.length > 0) {
        exercises.push({
          name: active ? active.name : ex.name,
          muscleGroup: ex.muscleGroup,
          img: (active && active.img) || ex.img,
          sets,
        });
      }
    });

    if (exercises.length > 0) {
      days.push({ name: day.name, short: day.short, exercises });
    }
  });

  return { days, volumeByGroup };
}

export function archiveWeek({ week, routine, progress, alternatives, startedAt }) {
  const { days, volumeByGroup } = snapshotCycle(routine, progress, alternatives);

  const totalSets = days.reduce(
    (acc, d) => acc + d.exercises.reduce((a, e) => a + e.sets.length, 0),
    0
  );
  const totalVolume = days.reduce(
    (acc, d) =>
      acc + d.exercises.reduce((a, e) => a + e.sets.reduce((x, s) => x + s.weight * s.reps, 0), 0),
    0
  );

  const entry = {
    week,
    startedAt: startedAt || null,
    completedAt: new Date().toISOString(),
    days,
    volumeByGroup,
    totalSets,
    totalVolume: Math.round(totalVolume),
  };

  writeJSON(DATA_KEYS.WEEKS, [...loadWeeks(), entry].slice(-MAX_WEEKS));
  return entry;
}

// Progreso del ciclo siguiente: se conservan peso y repeticiones como
// referencia de lo que se levantó, y se quitan las marcas de completado.
export function carryOverProgress(progress) {
  const next = {};
  for (const [key, value] of Object.entries(progress)) {
    if (!value) continue;
    if (!value.weight && !value.reps) continue;
    next[key] = { done: false, weight: value.weight || 0, reps: value.reps || 0 };
  }
  return next;
}
